import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { z } from 'zod';
import { AppRole, ALL_ROLES } from './roles';

// Single localStorage key — collapses the CMP tipmi_auth_tokens + tipmi_user_info + tipmi_user_roles (doc 03 §4.3).
export const STORAGE_KEY = 'tipmi_auth';

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: number | null; // epoch ms, decoded from JWT `exp`
  email: string;
  name: string;
  roles: AppRole[];
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  session: AuthSession | null;
  status: AuthStatus;
  setSession: (s: AuthSession) => void; // login callback / refresh success
  patchTokens: (
    t: Pick<AuthSession, 'accessToken' | 'refreshToken' | 'accessTokenExpiresAt'>,
  ) => void; // refresh: keep identity (roles/email/name)
  resolveStatus: () => void; // flip 'loading' -> resolved once hydration + callback settle
  clear: () => void; // logout / refresh-failure
}

// Validate the persisted blob on hydrate — a tampered/corrupt localStorage entry must not crash the app
// or inject privileges; fall back to unauthenticated (doc 03 §4.9). An invalid roles array fails closed to [].
const PersistedSession = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string(),
  accessTokenExpiresAt: z.number().nullable(),
  email: z.string(),
  name: z.string(),
  roles: z.array(z.enum(ALL_ROLES as [AppRole, ...AppRole[]])).catch([]),
});
const PersistedState = z.object({ session: PersistedSession.nullable() });

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,
      status: 'loading',
      setSession: (session) => set({ session, status: 'authenticated' }),
      patchTokens: (t) => {
        const cur = get().session;
        if (!cur) return; // never resurrect identity from a refresh alone
        set({ session: { ...cur, ...t } });
      },
      resolveStatus: () =>
        set((s) => ({ status: s.session?.accessToken ? 'authenticated' : 'unauthenticated' })),
      clear: () => set({ session: null, status: 'unauthenticated' }),
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({ session: s.session }),
      // Reject a tampered/corrupt persisted blob rather than trusting it (doc 03 §4.9).
      merge: (persisted, current) => {
        const parsed = PersistedState.safeParse(persisted);
        return parsed.success ? { ...current, session: parsed.data.session } : current;
      },
    },
  ),
);

/** Decode the JWT `exp` claim (seconds) -> epoch ms, or null if undecodable (doc 03 §4.4). */
export function decodeJwtExp(jwt: string): number | null {
  try {
    const part = jwt.split('.')[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

// Imperative accessors consumed by the orval mutator + 401 interceptor in api/http.ts (CONTRACTS.md §2).
export function getAccessToken(): string | null {
  return useAuthStore.getState().session?.accessToken ?? null;
}
export function getRefreshToken(): string | null {
  return useAuthStore.getState().session?.refreshToken ?? null;
}
export function setTokens(t: { accessToken: string; refreshToken: string }): void {
  useAuthStore.getState().patchTokens({
    accessToken: t.accessToken,
    refreshToken: t.refreshToken,
    accessTokenExpiresAt: decodeJwtExp(t.accessToken),
  });
}
export function clearTokens(): void {
  useAuthStore.getState().clear();
}
